/**
 * useQuery hook for the publication report-embeds listing (REPORTS tab).
 *
 * Replaces `publicationViewEffects.getPublicationReportEmbeds` (compose-app) —
 * the heaviest effect in the vertical. Faithfully mirrors its flow:
 *   1. `getPublicationStoryEmbeds` (postQuery) → raw embeds + aggregate count.
 *   2. If any embed lacks a `type`, the embeds are not yet populated: POST
 *      `.../populateEmbeds` to kick a background job and return that job id so
 *      the caller can poll + show the populating loader (result kind
 *      `populating`).
 *   3. Otherwise merge report metadata from `getReportsData` (nextgen) +
 *      `getSavedViewById`/`legacyReportById` (legacy) + `reportDataByIds`
 *      (deleted-view detection), build titles/isViewDeleted, sort, and return
 *      `{ kind: 'reports', count, data }`.
 *
 * Auth note (§1): non-admin reachable → `postQuery`/`transport` cookie auth.
 * Copy is passed in (`messages.reportOrViewDeleted`, `errorMessage`) per §8.
 * Pass `null` to disable before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';
import {
  getReportsData,
  getSavedViewById,
  legacyReportById,
  IReportDataResponse,
  ILegacySavedView,
  ILegacyReport
} from '@opengov/og-api-ts';
import {
  CHART_TYPE,
  DETAIL_TABLE_EMBED_REQUEST,
  REPORT_GRAPH,
  REPORT_TABLE,
  SHOW_USER,
  SORT_CREATED_NEW_TO_OLD,
  SORT_CREATED_OLD_TO_NEW,
  SORT_NAME_A_Z,
  SORT_NAME_Z_A,
  SORT_UPDATED_NEW_TO_OLD,
  SORT_UPDATED_OLD_TO_NEW,
  SUMMARY_TABLE_EMBED_REQUEST
} from '@opengov/stories-shared/constants';
import { getPublicationStoryEmbeds } from '@opengov/stories-shared/graphQL';

import { postQuery } from '../../../../effects/publications/postQuery';
import { getUserById } from '../../../../routes/hasura';
import { sortAlphabetically } from '../../../../utils';
import { useToast } from '../../../Toast/context/useToast.context';
import { IStoryEmbedSavedView, IStoryEmbedsResponse, StoryEmbedListData } from '../../../../../types';
import { publicationViewQueryKeys } from './publicationViewQueryKeys';

type IResponseEmbedSavedView = { data: IStoryEmbedSavedView[] };

export type PublicationReportEmbedsKeyParams = {
  publicationId: string;
  limit: number;
  offset: number;
  entityId: number;
  entitySubdomain: string;
  entityUuid?: string;
  sort?: string;
  type?: string;
  authorId?: string;
  folderIds?: string[];
  userId?: number;
};

export type UsePublicationReportEmbedsParams = PublicationReportEmbedsKeyParams | null;

export type PublicationReportEmbedsResult =
  | { kind: 'reports'; count: number; data: StoryEmbedListData[] }
  | { kind: 'populating'; populateStoryEmbedsJobId: string }
  | { kind: 'empty' };

export function usePublicationReportEmbeds(
  params: UsePublicationReportEmbedsParams,
  messages: { reportOrViewDeleted: string },
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useQuery<PublicationReportEmbedsResult, Error>({
    queryKey: params
      ? publicationViewQueryKeys.reportEmbeds(params)
      : ([...publicationViewQueryKeys.all, 'reportEmbeds', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { publicationId, limit, offset, type, authorId, folderIds, userId, sort, entityUuid } = params;
      const { entityId, entitySubdomain } = params;
      const author = await getUserById(userId);

      let variables = { publicationId, limit, offset } as {
        publicationId: string;
        limit: number;
        offset: number;
        sort?: string;
        type?: string[];
        authorId?: string;
        folderIds?: string[];
        order_by?: { [key: string]: string };
      };

      if (type === REPORT_GRAPH) {
        variables = { ...variables, type: Object.keys(CHART_TYPE).map((val) => CHART_TYPE[val]) as string[] };
      }
      if (type === REPORT_TABLE) {
        variables = { ...variables, type: [SUMMARY_TABLE_EMBED_REQUEST, DETAIL_TABLE_EMBED_REQUEST] };
      }
      if (authorId === SHOW_USER) {
        variables = { ...variables, authorId: author };
      }
      if (Array.isArray(folderIds)) {
        variables = { ...variables, folderIds };
      }
      if (sort && sort !== SORT_NAME_A_Z && sort !== SORT_NAME_Z_A) {
        switch (sort) {
          case SORT_UPDATED_OLD_TO_NEW:
            variables = { ...variables, order_by: { updatedAt: 'asc' } };
            break;
          case SORT_UPDATED_NEW_TO_OLD:
            variables = { ...variables, order_by: { updatedAt: 'desc' } };
            break;
          case SORT_CREATED_OLD_TO_NEW:
            variables = { ...variables, order_by: { createdAt: 'asc' } };
            break;
          case SORT_CREATED_NEW_TO_OLD:
            variables = { ...variables, order_by: { createdAt: 'desc' } };
            break;
          default:
            variables = { ...variables, order_by: { updatedAt: 'asc' } };
            break;
        }
      }

      const response: {
        storyEmbeds: StoryEmbedListData[];
        storyEmbeds_aggregate: { aggregate: { count: number } };
        errors?: { message: string }[];
      } = await postQuery(getPublicationStoryEmbeds, variables);

      const storyEmbeds = response?.storyEmbeds;
      if (!storyEmbeds) {
        return Promise.reject(response.errors);
      }
      if (!storyEmbeds.length) {
        return { kind: 'empty' };
      }

      const storyResponseWithTypes = storyEmbeds.filter((embed) => !!embed.type);
      const shouldPopulateReportEmbeds = storyResponseWithTypes.length !== storyEmbeds.length;

      if (shouldPopulateReportEmbeds) {
        const populateJobResponse = await transport
          .url(`/api/stories/v1/publications/${publicationId}/populateEmbeds`)
          .json({ entityId, entitySubdomain, entityUuid })
          .post()
          .json();
        return {
          kind: 'populating',
          populateStoryEmbedsJobId: populateJobResponse?.populatedStoryEmbedsJobId
        };
      }

      const storyEmbedsMap = new Map<string, IStoryEmbedsResponse>(
        storyResponseWithTypes.map((record) => [record.savedViewId, record])
      );
      if (!storyEmbedsMap.size) {
        return { kind: 'reports', count: 0, data: [] };
      }

      const reportDataFetchPayload = { savedViewIds: [...storyEmbedsMap.keys()] as string[] };
      const reportData = (await getReportsData(reportDataFetchPayload)) as IReportDataResponse;
      const nextGenSavedViews = reportData.data.map((report) => report.savedViewId);
      const legacySavedViews = reportDataFetchPayload.savedViewIds.filter(
        (savedView) => !nextGenSavedViews.includes(savedView)
      );

      let legacyReportAndSavedView: {
        reportId: string;
        savedViewId: string;
        reportTitle: string;
        savedViewTitle: string;
      }[] = [];
      let legacySavedViewsData: ILegacySavedView[] = [];
      if (Array.isArray(legacySavedViews) && legacySavedViews.length > 0) {
        legacySavedViewsData = await Promise.all(
          legacySavedViews.map(async (savedViewId) => {
            try {
              return await getSavedViewById(savedViewId);
            } catch (e) {
              return null;
            }
          })
        ).catch(() => []);
        legacySavedViewsData = legacySavedViewsData.filter((data) => data);

        let legacyReportsData: ILegacyReport[] = [];
        if (Array.isArray(legacySavedViewsData) && legacySavedViewsData.length > 0) {
          const reportIds = legacySavedViewsData
            .filter((savedView) => !!savedView.report_id)
            .map((savedView) => savedView.report_id);
          legacyReportsData = await Promise.all(
            reportIds.map(async (reportId) => legacyReportById(reportId))
          ).catch(() => []);
        }

        if (legacyReportsData.length > 0) {
          const legacyReportMap = Object.fromEntries(legacyReportsData.map((report) => [report.id, report]));
          legacyReportAndSavedView = legacySavedViewsData.map((savedView) => ({
            reportId: legacyReportMap[savedView.report_id] ? legacyReportMap[savedView.report_id].uuid : '',
            savedViewId: savedView.uuid,
            reportTitle: legacyReportMap[savedView.report_id] ? legacyReportMap[savedView.report_id].name : '',
            savedViewTitle: savedView.description
          }));
        }
      }

      const currentAndLegacyReportData = [...reportData.data, ...legacyReportAndSavedView];
      // reportTitle is `string | IReport` across the merged nextgen+legacy
      // shapes; the retired effect read it untyped. Keep `any` to preserve the
      // `reportTitle?.title || reportTitle` fallback exactly.
      const reportMap: Record<string, any> = Object.fromEntries(
        currentAndLegacyReportData.map((report) => [report.savedViewId, report])
      );
      const savedViewIds = storyResponseWithTypes.map((embed) => embed.savedViewId);
      const savedViews: IResponseEmbedSavedView = await transport
        .url(`/api/reporting_service/v2/reports/reportDataByIds`)
        .json({ savedViewIds })
        .post()
        .json();
      const deletedSavedViews = savedViews?.data
        .filter((view) => view.isViewDeleted === true)
        .map((view) => view.savedViewId);

      let finalData: StoryEmbedListData[] = storyResponseWithTypes.map((embed) => {
        const isViewDeleted = deletedSavedViews.includes(embed.savedViewId) || !reportMap[embed.savedViewId];
        const title: string = reportMap[embed.savedViewId]
          ? reportMap[embed.savedViewId]?.reportTitle?.title || reportMap[embed.savedViewId].reportTitle
          : messages.reportOrViewDeleted;
        const savedViewTitle: string = reportMap[embed.savedViewId]
          ? reportMap[embed.savedViewId].savedViewTitle
          : messages.reportOrViewDeleted;
        return { ...embed, isViewDeleted, title, savedViewTitle };
      });
      if (sort) {
        finalData = sortAlphabetically(finalData, sort);
      }

      return {
        kind: 'reports',
        count: response?.storyEmbeds_aggregate?.aggregate?.count,
        data: finalData
      };
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
